import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useApi } from "../hooks/useApi";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const PORTAL_URL =
  import.meta.env.VITE_PORTAL_URL || "http://localhost:5422";
const UI_AUTH0_CLIENT_ID =
  import.meta.env.VITE_AUTH0_CLIENT_ID || "";

export default function AdminAccounts() {
  const api = useApi();
  const navigate = useNavigate();
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [refreshing, setRefreshing] = React.useState(false);
  const [pagination, setPagination] = React.useState({
    total: 0,
    limit: 20,
    offset: 0,
    page: 1,
  });
  
  // Add Account Modal State
  const [createAccountDialogOpen, setCreateAccountDialogOpen] = React.useState(false);
  const [newAccountEmail, setNewAccountEmail] = React.useState("");
  const [newAccountPassword, setNewAccountPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [creatingAccount, setCreatingAccount] = React.useState(false);
  const [createAccountError, setCreateAccountError] = React.useState(null);
  


  const fetchAccounts = React.useCallback(async (search = "", offset = 0, showRefresh = false, limit = null) => {
    if (showRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search && search.trim()) {
        params.append("search", search.trim());
      }
      const limitToUse = limit !== null ? limit : pagination.limit;
      params.append("limit", limitToUse.toString());
      params.append("offset", offset.toString());

      const data = await api.get(`/admin/accounts?${params.toString()}`);
      setAccounts(data.data || []);
      const paginationData = data.pagination || {};
      const total = paginationData.total || 0;
      const currentOffset = paginationData.offset || offset;
      const limitUsed = limit !== null ? limit : pagination.limit;
      const page = Math.floor(currentOffset / limitUsed) + 1;
      
      setPagination((prev) => ({
        ...prev,
        total,
        offset: currentOffset,
        page,
        limit: limitUsed,
        hasMore: paginationData.hasMore || false,
      }));
    } catch (err) {
      console.error("Error fetching Auth0 accounts:", err);
      setError(err.message || "Failed to fetch Auth0 accounts");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, pagination.limit]);

  React.useEffect(() => {
    fetchAccounts(searchQuery, 0);
  }, [fetchAccounts]);

  // Debounced search - reset to page 1 when searching
  const searchTimeoutRef = React.useRef(null);
  React.useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      // Reset to first page when search changes
      setPagination((prev) => ({ ...prev, page: 1, offset: 0 }));
      fetchAccounts(searchQuery, 0);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, fetchAccounts]);


  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * pagination.limit;
    fetchAccounts(searchQuery, newOffset);
  };
  
  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    // Fetch with new limit and reset to page 1
    fetchAccounts(searchQuery, 0, false, newLimit);
  };
  
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const handleCreateAccount = async () => {
    if (!newAccountEmail.trim()) {
      setCreateAccountError("Email address is required");
      return;
    }

    // Validate password if provided
    if (newAccountPassword.trim() && newAccountPassword.trim().length < 8) {
      setCreateAccountError("Password must be at least 8 characters");
      return;
    }

    // Check for duplicate email in current list
    const duplicate = accounts.find(
      (acc) => acc.email && acc.email.toLowerCase() === newAccountEmail.trim().toLowerCase()
    );
    if (duplicate) {
      setCreateAccountError(`An account with email "${newAccountEmail.trim()}" already exists`);
      return;
    }

    setCreatingAccount(true);
    setCreateAccountError(null);

    try {
      const payload = {
        email: newAccountEmail.trim(),
      };
      if (newAccountPassword.trim()) {
        payload.password = newAccountPassword.trim();
      }

      await api.post("/admin/accounts", payload);

      // Close dialog and reset form
      setCreateAccountDialogOpen(false);
      setNewAccountEmail("");
      setNewAccountPassword("");
      setShowPassword(false);
      setCreateAccountError(null);

      // Refresh the accounts list
      fetchAccounts(searchQuery, pagination.offset);
    } catch (err) {
      console.error("Error creating Auth0 account:", err);
      setCreateAccountError(err.message || "Failed to create Auth0 account");
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleCloseCreateAccountDialog = () => {
    if (!creatingAccount) {
      setCreateAccountDialogOpen(false);
      setNewAccountEmail("");
      setNewAccountPassword("");
      setShowPassword(false);
      setCreateAccountError(null);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Auth0 Accounts
                </Typography>
              </Breadcrumbs>
            </Box>
            <AdminPageHeader
              title="Auth0 Accounts"
              description="View and manage all Auth0 accounts in the system"
              icon={<AccountCircleIcon sx={{ color: "primary.main" }} />}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchAccounts(searchQuery, pagination.offset, true)}
                  disabled={refreshing || loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setCreateAccountDialogOpen(true);
                    setNewAccountEmail("");
                    setNewAccountPassword("");
                    setShowPassword(false);
                    setCreateAccountError(null);
                  }}
                >
                  Add Account
                </Button>
              </Box>
            </AdminPageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Card>
              <CardContent>
                {/* Top pagination controls */}
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Skeleton height={20} width={180} />
                      <Skeleton height={40} width={120} borderRadius={1} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Skeleton height={32} width={80} borderRadius={1} />
                      <Skeleton height={20} width={150} />
                      <Skeleton height={32} width={60} borderRadius={1} />
                    </Box>
                  </Box>
                ) : pagination.total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, pagination.total)} of{" "}
                        {pagination.total} accounts
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {pagination.page} of {totalPages} ({pagination.total} total)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= totalPages || loading}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
                
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search Auth0 accounts by email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: 300 }}
                  />
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Email Verified</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Last Login</TableCell>
                        <TableCell>Logins</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: 20 }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={24} width={80} borderRadius={4} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={24} width={80} borderRadius={4} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} width={60} />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5 }}>
                              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Skeleton height={32} width={120} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : accounts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography color="text.secondary">
                              {searchQuery ? "No Auth0 accounts found matching your search" : "No Auth0 accounts found"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        accounts.map((account) => (
                          <TableRow key={account.id}>
                            <TableCell>{account.email || "N/A"}</TableCell>
                            <TableCell>{account.name || "N/A"}</TableCell>
                            <TableCell>
                              <Chip
                                label={account.blocked ? "Blocked" : "Active"}
                                color={account.blocked ? "error" : "success"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={account.email_verified ? "Verified" : "Unverified"}
                                color={account.email_verified ? "success" : "warning"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {account.created_at
                                ? new Date(account.created_at).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              {account.last_login
                                ? new Date(account.last_login).toLocaleDateString()
                                : "Never"}
                            </TableCell>
                            <TableCell>{account.logins_count || 0}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => navigate(`/dashboard/accounts/${account.id}`)}
                                >
                                  View Details
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Skeleton height={20} width={180} />
                      <Skeleton height={40} width={120} borderRadius={1} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Skeleton height={32} width={80} borderRadius={1} />
                      <Skeleton height={20} width={150} />
                      <Skeleton height={32} width={60} borderRadius={1} />
                    </Box>
                  </Box>
                ) : pagination.total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, pagination.total)} of{" "}
                        {pagination.total} accounts
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {pagination.page} of {totalPages} ({pagination.total} total)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= totalPages || loading}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>

      {/* Create Account Dialog */}
      <Dialog open={createAccountDialogOpen} onClose={handleCloseCreateAccountDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Auth0 Account</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {createAccountError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createAccountError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            required
            value={newAccountEmail}
            onChange={(e) => {
              setNewAccountEmail(e.target.value);
              setCreateAccountError(null);
            }}
            margin="dense"
            size="small"
            placeholder="user@example.com"
            error={!!createAccountError}
            helperText={createAccountError || "Enter the email address for the new Auth0 account"}
            disabled={creatingAccount}
            autoFocus
          />
          <TextField
            fullWidth
            label="Password (Optional)"
            type={showPassword ? "text" : "password"}
            value={newAccountPassword}
            onChange={(e) => {
              setNewAccountPassword(e.target.value);
              setCreateAccountError(null);
            }}
            margin="dense"
            size="small"
            placeholder="Leave empty for passwordless login"
            disabled={creatingAccount}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={creatingAccount}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Password must be at least 8 characters if provided"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateAccountDialog} disabled={creatingAccount}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateAccount}
            variant="contained"
            disabled={!newAccountEmail.trim() || creatingAccount || (newAccountPassword.trim() && newAccountPassword.trim().length < 8)}
            startIcon={creatingAccount ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {creatingAccount ? "Creating..." : "Create Account"}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}
